import type { AdminQuizDetailsContent } from '@/features/Quiz/utils/quizV2AdminEdit';
import { buildQuizV2UpdatePayload } from '@/features/Quiz/utils/quizV2AdminEdit';
import type {
    CreateLibraryQuizV2RequestBody,
    LibraryQuizDetailsV2,
    UpdateLibraryQuizV2RequestBody,
} from '@/features/Quiz/api/libraryQuizDetailV2';
import type { QuizFormData } from '@/features/Quiz/types';
import { buildQuizV2CreatePayload } from '@/features/Quiz/utils/buildQuizV2CreatePayload';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isQuizUuid = (value: string | undefined): value is string =>
    !!value && UUID_PATTERN.test(value);

/** Shared React Query key for raw GET library V2 details (view / edit / copy). */
export const getLibraryQuizV2QueryKey = (quizUuid: string) => `getLibraryQuizV2-${quizUuid}`;

export const libraryQuizDetailsToAdminContent = (
    detail: LibraryQuizDetailsV2
): AdminQuizDetailsContent => {
    const noOfQuestions = detail.sections.reduce(
        (count, section) =>
            count + section.items.filter((item) => item.type === 'QUESTION').length,
        0
    );

    return {
        quiz: {
            id: detail.quiz.id,
            uuid: detail.quiz.uuid,
            title: detail.quiz.title,
            type: detail.quiz.type,
            status: detail.quiz.status,
            startTime: detail.quiz.startTime,
            endTime: detail.quiz.endTime,
            durationInMinutes: detail.quiz.durationInMinutes,
            maxAttemptCount: detail.quiz.maxAttemptCount,
            evaluationType: detail.quiz.evaluationType,
            accessType: detail.quiz.accessType,
            creatorType: detail.quiz.creatorType,
        },
        sections: detail.sections,
        noOfQuestions,
    };
};

export type LibraryQuizV2Meta = {
    libraryType: string;
    joveSectionId: number | null;
    joveDomainId?: number | null;
    locale?: string | null;
};

export const buildLibraryQuizV2CreatePayload = (
    values: QuizFormData,
    libraryMeta: LibraryQuizV2Meta
): CreateLibraryQuizV2RequestBody => {
    const base = buildQuizV2CreatePayload(values);
    const articleId =
        libraryMeta.libraryType === 'VIDEO'
            ? libraryMeta.joveDomainId ?? values.article ?? null
            : null;

    const sections =
        articleId != null
            ? base.sections.map((section) => ({
                  ...section,
                  sectionType: 'ARTICLE_LINKED_SECTION' as const,
                  articleId: Number(articleId),
              }))
            : base.sections;

    return {
        ...base,
        quiz: { ...base.quiz, status: values.status ?? 'ACTIVE' },
        sections,
        libraryType: libraryMeta.libraryType,
        joveSectionId: libraryMeta.joveSectionId ?? null,
        joveDomainId: libraryMeta.joveDomainId ?? null,
        locale: libraryMeta.locale ?? 'en',
    };
};

export const buildLibraryQuizV2UpdatePayload = (
    values: QuizFormData,
    libraryDetails: LibraryQuizDetailsV2
): UpdateLibraryQuizV2RequestBody => {
    const base = buildQuizV2UpdatePayload(values, libraryQuizDetailsToAdminContent(libraryDetails));

    return {
        ...base,
        quiz: { ...base.quiz, status: values.status ?? base.quiz.status },
        libraryType: libraryDetails.libraryType,
        joveSectionId: libraryDetails.joveSectionId,
        locale: libraryDetails.locale,
    };
};
